import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiPlay, FiSkipForward, FiZap } from 'react-icons/fi';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${({ theme }) =>
    theme?.body === '#fafbfc'
      ? 'rgba(255, 255, 255, 0.85)'
      : 'rgba(0, 0, 0, 0.75)'};
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease;
`;

const Card = styled.div`
  background: ${({ theme }) => theme?.card || '#1a1d24'};
  border: 1px solid ${({ theme }) => theme?.border || 'rgba(255,255,255,0.08)'};
  border-radius: 24px;
  width: 520px;
  max-width: 95vw;
  padding: 48px 44px 40px;
  text-align: center;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5), 0 0 120px rgba(16, 185, 129, 0.06);
  animation: ${slideUp} 0.4s ease;
`;

const DemoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 12px;
  background: ${({ theme }) => (theme?.primary || '#10b981') + '10'};
  border: 1px solid ${({ theme }) => (theme?.primary || '#10b981') + '20'};
  color: ${({ theme }) => theme?.primary || '#10b981'};
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 24px;
`;

const Logo = styled.div`
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme?.text || '#e5e7eb'};
  margin-bottom: 8px;

  span {
    color: ${({ theme }) => theme?.primary || '#10b981'};
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme?.text || '#e5e7eb'};
  margin: 0 0 12px 0;
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme?.text || '#e5e7eb'};
  opacity: 0.45;
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 32px;
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme?.border || 'rgba(255,255,255,0.06)'};
  background: ${({ theme }) => theme?.contrast || 'rgba(255,255,255,0.02)'};
  font-size: 0.88rem;
  color: ${({ theme }) => theme?.text || '#e5e7eb'};
  opacity: 0.8;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme?.primary || '#10b981'};
    flex-shrink: 0;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  padding: 14px 28px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
  }
`;

const SecondaryButton = styled.button`
  padding: 14px 24px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme?.border || 'rgba(255,255,255,0.08)'};
  background: transparent;
  color: ${({ theme }) => theme?.text || '#e5e7eb'};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  opacity: 0.6;

  &:hover { opacity: 1; }
`;

const FEATURES = [
  'Ambiente seguro com dados fictícios',
  'Explore todas as funcionalidades',
  'Aprenda com um guia passo a passo',
  'Saia a qualquer momento',
];

export default function DemoWelcome({ onStartTutorial, onSkip, theme }) {
  return (
    <Overlay theme={theme}>
      <Card theme={theme}>
        <DemoBadge theme={theme}>
          <FiZap size={14} /> Modo Demonstração
        </DemoBadge>

        <Logo theme={theme}>
          Vi<span>dal</span>
        </Logo>

        <Title theme={theme}>Bem-vindo ao Vidal</Title>
        <Subtitle theme={theme}>
          Você está no modo demonstração. Todos os dados são fictícios e servem apenas para você
          conhecer as funcionalidades da plataforma.
        </Subtitle>

        <FeatureList>
          {FEATURES.map((text, i) => (
            <FeatureItem key={i} theme={theme}>
              {text}
            </FeatureItem>
          ))}
        </FeatureList>

        <ButtonRow>
          <SecondaryButton theme={theme} onClick={onSkip}>
            <FiSkipForward size={16} />
            Pular
          </SecondaryButton>
          <PrimaryButton onClick={onStartTutorial}>
            <FiPlay size={16} />
            Iniciar Tutorial
          </PrimaryButton>
        </ButtonRow>
      </Card>
    </Overlay>
  );
}
